import React, { useState } from 'react';
import { FileText, Copy, Shuffle, CheckCircle } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { CreatePOrderForm } from './CreatePOrderForm';
import { CopyPOrderDialog } from './CopyPOrderDialog';
import { RandomPOrderDialog } from './RandomPOrderDialog';

type TabType = 'create' | 'copy' | 'random';

interface POrderRegistrationProps {
  onBack?: () => void;
}

export const POrderRegistration: React.FC<POrderRegistrationProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<TabType>('create');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSuccess = (orderNo: number, message?: string) => {
    setSuccessMessage(message || `Innkjøpsordre ${orderNo} er opprettet!`);

    // Clear success message after 5 seconds
    setTimeout(() => {
      setSuccessMessage(null);
    }, 5000);
  };

  const tabs = [
    {
      id: 'create' as TabType,
      label: 'Opprett Ny Innkjøpsordre',
      icon: FileText,
      description: 'Opprett en ny innkjøpsordre manuelt'
    },
    {
      id: 'copy' as TabType,
      label: 'Kopier Innkjøpsordre',
      icon: Copy,
      description: 'Kopier en eksisterende innkjøpsordre'
    },
    {
      id: 'random' as TabType,
      label: 'Generer Test-innkjøpsordre',
      icon: Shuffle,
      description: 'Generer en tilfeldig test-innkjøpsordre'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <BackButton onClick={onBack || (() => window.history.back())} />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Innkjøpsordreregistrering</h1>
              <p className="text-sm text-gray-600 mt-1">
                Opprett, kopier eller generer nye innkjøpsordre
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
            <div className="text-green-800 font-medium">
              {successMessage}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex-1 flex items-center justify-center gap-2 px-6 py-4 border-b-2 font-medium text-sm
                      transition-colors duration-200
                      ${isActive
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon size={20} />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Tab Description */}
            <div className="mb-6">
              <p className="text-gray-600">
                {tabs.find(t => t.id === activeTab)?.description}
              </p>
            </div>

            {/* Create POrder Tab */}
            {activeTab === 'create' && (
              <CreatePOrderForm onSuccess={(orderNo) => handleSuccess(orderNo)} />
            )}

            {/* Copy POrder Tab */}
            {activeTab === 'copy' && (
              <CopyPOrderDialog onSuccess={(orderNo) => handleSuccess(orderNo, `Innkjøpsordre ${orderNo} er kopiert!`)} />
            )}

            {/* Random POrder Tab */}
            {activeTab === 'random' && (
              <RandomPOrderDialog onSuccess={(orderNo) => handleSuccess(orderNo, `Test-innkjøpsordre ${orderNo} er generert!`)} />
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Hjelp og veiledning</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-blue-800">
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <FileText size={16} />
                Opprett Ny Innkjøpsordre
              </h4>
              <p className="text-xs leading-relaxed">
                Opprett en ny innkjøpsordre ved å fylle ut ordreinfo, leverandørinfo og mottaksadresse.
                Legg til ordrelinjer ved å velge varer fra dropdown-listen.
                Systemet fyller automatisk ut beskrivelse, enhet og lokasjon når du velger en vare.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Copy size={16} />
                Kopier Innkjøpsordre
              </h4>
              <p className="text-xs leading-relaxed">
                Søk etter en eksisterende innkjøpsordre ved å skrive inn ordrenummeret.
                Systemet viser ordreinformasjon og linjer.
                Klikk på "Kopier Innkjøpsordre" for å lage en kopi med nytt ordrenummer.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Shuffle size={16} />
                Generer Test-innkjøpsordre
              </h4>
              <p className="text-xs leading-relaxed">
                Generer en tilfeldig test-innkjøpsordre med varer fra lageret.
                Velg antall linjer og lager for innkjøpsordren.
                Nyttig for testing av mottak- og innleggingsoperasjoner.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POrderRegistration;
